import { useTranslation } from "react-i18next";
import {
  confidenceLabel,
  explanationScore,
  explanationStatusLabel,
  explanationUnavailableLabel,
  type MatchConfidence,
  type MatchExplanationLine,
  STATUS_ICONS,
  topExplanationLines,
} from "../../lib/matchExplanation";
import "./MatchExplanation.css";

interface MatchExplanationProps {
  lines: readonly MatchExplanationLine[] | null | undefined;
  confidence?: MatchConfidence | null;
  /** Compact card summary: only the `limit` most-weighted lines, no weights. */
  compact?: boolean;
  limit?: number;
  /** Moderators: also show each criterion's buyer weight. */
  showWeights?: boolean;
  title?: string;
}

/** "Why this match" — one line per criterion: what matched, what was missing, how sure. */
const MatchExplanation = ({
  lines,
  confidence,
  compact = false,
  limit = 3,
  showWeights = false,
  title: titleProp,
}: MatchExplanationProps) => {
  const { t } = useTranslation("components");
  const title = titleProp ?? t("matchExplanation.title");
  const all = lines ?? [];
  const shown = compact ? topExplanationLines(all, limit) : all;

  return (
    <section className={`match-explanation${compact ? " match-explanation--compact" : ""}`} aria-label={title}>
      <div className="match-explanation__header">
        <span className="match-explanation__title">{title}</span>
        {confidence ? (
          <span className={`match-explanation__confidence match-explanation__confidence--${confidence}`}>
            {confidenceLabel(confidence)}
          </span>
        ) : null}
      </div>
      {shown.length === 0 ? (
        <p className="match-explanation__empty">{explanationUnavailableLabel()}</p>
      ) : (
        <ul className="match-explanation__list">
          {shown.map((line) => {
            const score = explanationScore(line);
            return (
              <li
                key={line.criterion}
                className={`match-explanation__line match-explanation__line--${line.status}`}
                title={`${explanationStatusLabel(line.status)} · ${confidenceLabel(line.confidence)}`}
              >
                <i className={`match-explanation__icon ${STATUS_ICONS[line.status]}`} aria-hidden="true" />
                <span className="match-explanation__text">
                  <span className="match-explanation__label">
                    {line.label}
                    <span className="sr-only"> — {explanationStatusLabel(line.status)}</span>
                  </span>
                  <span className="match-explanation__detail">{line.detail}</span>
                </span>
                <span className="match-explanation__meta">
                  {score ? <span className="match-explanation__score">{score}</span> : null}
                  {showWeights ? (
                    <span className="match-explanation__weight">
                      {t("matchExplanation.weight", { percent: Math.round(line.weight * 100) })}
                    </span>
                  ) : null}
                  {!compact ? (
                    <span
                      className={`match-explanation__line-confidence match-explanation__line-confidence--${line.confidence}`}
                    >
                      {confidenceLabel(line.confidence)}
                    </span>
                  ) : null}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default MatchExplanation;
